import { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import parse from 'html-react-parser';
import { NumericFormat } from 'react-number-format';
import AlertBox from './AlertBox';
import type {
    ExpenseTableBilag,
    ExpenseTableColumn,
    ExpenseTableElementType,
    ExpenseTableRow,
} from '../helpers/types';
import { fileIcon } from '../helpers/utils';

type Props = {
    value: ExpenseTableRow[];
    onChange: (rows: ExpenseTableRow[]) => void;
    error?: string | null;
    // Forhåndsdefinerte (låste) rader basert på andre number-felter i skjemaet.
    // `value` resolves av FormElement fra formData (live). Label vises i første
    // kolonne, value i totalsum-kolonnen. onEditPrefilled navigerer til feltet.
    prefilledRows?: Array<{ field: string; label: string; value: string | number }>;
    onEditPrefilled?: (field: string) => void;
};

// Defaultkolonner brukes når malen ikke spesifiserer columns – matcher det
// gamle hardkodede oppsettet (beskrivelse + belop + bilag) for bakoverkompabilitet.
const DEFAULT_COLUMNS: ExpenseTableColumn[] = [
    { name: 'beskrivelse', label: 'Beskrivelse', type: 'text' },
    { name: 'belop', label: 'Beløp (kr)', type: 'number' },
    { name: 'bilag', label: 'Bilag', type: 'attachment' },
];

const isAttachment = (val: unknown): val is File | ExpenseTableBilag =>
    val instanceof File ||
    (typeof val === 'object' && val !== null && typeof (val as ExpenseTableBilag).name === 'string');

const cellHasValue = (col: ExpenseTableColumn, val: unknown): boolean => {
    if (col.type === 'attachment') return !!val;
    if (col.type === 'number') {
        if (typeof val === 'number') return val !== 0 || String(val).trim() !== '';
        return String(val ?? '').trim() !== '';
    }
    return String(val ?? '').trim() !== '';
};

const buildEmptyRow = (columns: ExpenseTableColumn[]): ExpenseTableRow => {
    const row: ExpenseTableRow = {};
    columns.forEach((col) => {
        row[col.name] = col.type === 'attachment' ? null : '';
    });
    return row;
};

const ExpenseTable = (props: ExpenseTableElementType & Props) => {
    const { maxRows, value: rows, onChange, error, label, description } = props;
    const columns: ExpenseTableColumn[] = props.columns?.length ? props.columns : DEFAULT_COLUMNS;

    // Velger total-kolonne: eksplisitt totalColumn fra mal, ellers første number-kolonne,
    // ellers ingen (skjul totalsum).
    const totalColumnName =
        props.totalColumn && columns.find((c) => c.name === props.totalColumn && c.type === 'number')
            ? props.totalColumn
            : columns.find((c) => c.type === 'number')?.name;

    const fileInputRefs = useRef<Record<string, (HTMLInputElement | null)>>({});
    const [addError, setAddError] = useState<string | null>(null);

    const prefilled = props.prefilledRows ?? [];

    const userTotal = totalColumnName
        ? rows.reduce((sum, row) => {
              const raw = row[totalColumnName];
              return sum + (Number(String(raw ?? '').replace(/\s+/g, '')) || 0);
          }, 0)
        : 0;
    // Forhåndsdefinerte rader teller med i totalsummen (verdien ligger i totalkolonnen).
    const prefilledTotal = totalColumnName
        ? prefilled.reduce((sum, p) => sum + (Number(String(p.value ?? '').replace(/\s+/g, '')) || 0), 0)
        : 0;
    const total = userTotal + prefilledTotal;
    const totalPoster = prefilled.length + rows.length;
    const hasAnyRows = prefilled.length > 0 || rows.length > 0;

    const updateCell = (index: number, key: string, val: ExpenseTableRow[string]) => {
        setAddError(null);
        onChange(rows.map((row, i) => (i === index ? { ...row, [key]: val } : row)));
    };

    const removeRow = (index: number) => {
        setAddError(null);
        onChange(rows.filter((_, i) => i !== index));
    };

    const canAddRow = !maxRows || rows.length < maxRows;

    const cellMinWidth = (col: ExpenseTableColumn) =>
        col.type === 'number' ? 140 : col.type === 'attachment' ? 180 : undefined;

    return (
        <div className="expense-table">
            <label className="form-label">{label}</label>
            {description && <div className="text-muted small mb-2">{parse(description)}</div>}
            {hasAnyRows && (
                <div className="d-flex align-items-center gap-3 pb-2 mb-3 border-2 border-bottom border-dark-subtle">
                    {columns.map((col) => {
                        const minWidth = cellMinWidth(col);
                        return (
                            <div
                                key={col.name}
                                className={minWidth ? '' : 'flex-grow-1'}
                                style={minWidth ? { minWidth } : undefined}
                            >
                                <small className="text-muted fw-semibold">{col.label}</small>
                            </div>
                        );
                    })}
                    <div style={{ width: 36 }} />
                </div>
            )}

            {prefilled.map((p, pIndex) => (
                <div key={`pre-${pIndex}`} className="d-flex align-items-center gap-3 mb-3">
                    {columns.map((col, colIdx) => {
                        const minWidth = cellMinWidth(col);
                        const cellStyle = minWidth ? { minWidth } : undefined;
                        const cellClass = minWidth ? '' : 'flex-grow-1';

                        // Label i første kolonne, value i totalsum-kolonnen, resten tomt.
                        if (colIdx === 0) {
                            return (
                                <div key={col.name} className={cellClass} style={cellStyle}>
                                    <input type="text" className="form-control" value={p.label} disabled readOnly />
                                </div>
                            );
                        }
                        if (col.name === totalColumnName) {
                            return (
                                <div key={col.name} className={cellClass} style={cellStyle}>
                                    <NumericFormat
                                        value={p.value !== '' && p.value !== null && p.value !== undefined ? p.value : ''}
                                        thousandSeparator=" "
                                        className="form-control text-end"
                                        placeholder="0"
                                        disabled
                                        readOnly
                                    />
                                </div>
                            );
                        }
                        return <div key={col.name} className={cellClass} style={cellStyle} />;
                    })}
                    {props.onEditPrefilled ? (
                        <button
                            type="button"
                            className="btn btn-link text-primary p-1 flex-shrink-0"
                            style={{ width: 36 }}
                            onClick={() => props.onEditPrefilled?.(p.field)}
                            title="Rediger"
                        >
                            <FontAwesomeIcon icon={['fal', 'pen']} />
                        </button>
                    ) : (
                        <div style={{ width: 36 }} />
                    )}
                </div>
            ))}

            {rows.map((row, index) => (
                <div key={index} className="d-flex align-items-center gap-3 mb-3">
                    {columns.map((col) => {
                        const minWidth = cellMinWidth(col);
                        const cellStyle = minWidth ? { minWidth } : undefined;
                        const cellClass = minWidth ? '' : 'flex-grow-1';
                        const value = row[col.name];

                        if (col.type === 'number') {
                            return (
                                <div key={col.name} className={cellClass} style={cellStyle}>
                                    <NumericFormat
                                        value={value !== '' && value !== null && value !== undefined ? (value as string | number) : ''}
                                        onValueChange={(v) => updateCell(index, col.name, v.value)}
                                        thousandSeparator=" "
                                        className="form-control text-end"
                                        placeholder="0"
                                    />
                                </div>
                            );
                        }

                        if (col.type === 'attachment') {
                            const inputKey = `${index}-${col.name}`;
                            const attached = isAttachment(value) ? value : null;
                            return (
                                <div key={col.name} className={cellClass} style={cellStyle}>
                                    <input
                                        type="file"
                                        className="d-none"
                                        ref={(el) => {
                                            fileInputRefs.current[inputKey] = el;
                                        }}
                                        onChange={(e) => {
                                            // Fila lastes opp ved neste saveDraft, sammen med
                                            // resten av skjemaet. API-et legger den i
                                            // `attachments` og gir raden en referanse tilbake.
                                            updateCell(index, col.name, e.target.files?.[0] ?? null);
                                            e.target.value = '';
                                        }}
                                    />
                                    {attached ? (() => {
                                        const name = attached instanceof File ? attached.name : attached.name;
                                        const url = attached instanceof File ? null : attached.download_url;
                                        return (
                                            <div className="d-flex align-items-center gap-1 small">
                                                <FontAwesomeIcon icon={fileIcon(name.split('.').pop() ?? '')} className="flex-shrink-0" />
                                                {url ? (
                                                    <a href={url} target="_blank" rel="noreferrer" className="text-primary text-truncate text-decoration-none" style={{ maxWidth: 110 }}>{name}</a>
                                                ) : (
                                                    <span className="text-primary text-truncate" style={{ maxWidth: 110 }}>{name}</span>
                                                )}
                                                <button
                                                    type="button"
                                                    className="btn btn-link text-danger p-0 ms-1 flex-shrink-0"
                                                    onClick={() => updateCell(index, col.name, null)}
                                                    title="Fjern bilag"
                                                >
                                                    <FontAwesomeIcon icon={['fal', 'times']} />
                                                </button>
                                            </div>
                                        );
                                    })() : (
                                        <button
                                            type="button"
                                            className="btn btn-link text-primary p-0 text-decoration-none"
                                            onClick={() => fileInputRefs.current[inputKey]?.click()}
                                        >
                                            <FontAwesomeIcon icon={['fas', 'upload']} className="me-1" />
                                            Last opp bilag
                                        </button>
                                    )}
                                </div>
                            );
                        }

                        // text (default)
                        return (
                            <div key={col.name} className={cellClass} style={cellStyle}>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={String(value ?? '')}
                                    onChange={(e) => updateCell(index, col.name, e.target.value)}
                                    placeholder={col.label}
                                />
                            </div>
                        );
                    })}
                    <button
                        type="button"
                        className="btn btn-link text-danger p-1 flex-shrink-0"
                        style={{ width: 36 }}
                        onClick={() => removeRow(index)}
                        title="Fjern rad"
                    >
                        <FontAwesomeIcon icon={['fal', 'trash-can']} />
                    </button>
                </div>
            ))}

            {canAddRow ? (
                <>
                    <button
                        type="button"
                        className="btn btn-link text-primary ps-0 text-decoration-none"
                        onClick={() => {
                            const incomplete = rows.some((row) =>
                                columns.some((col) => !cellHasValue(col, row[col.name]))
                            );
                            if (incomplete) {
                                setAddError('Alle felter på en rad må fylles inn');
                                return;
                            }
                            setAddError(null);
                            onChange([...rows, buildEmptyRow(columns)]);
                        }}
                    >
                        + {props.addButtonText ?? 'Legg til rad'}
                    </button>
                    {addError && <p className="text-danger small mt-1 mb-0">{addError}</p>}
                </>
            ) : (
                <p className="text-muted small mb-2">Maks {maxRows} rader nådd.</p>
            )}

            {hasAnyRows && totalColumnName && (
                <div className="d-flex justify-content-end mt-3 mb-2 border-2 border-top border-dark-subtle">
                    <div className="text-end mt-2">
                        <div className="text-muted small">Totalsum</div>
                        <div className="fs-5 fw-bold">Kr. {total.toLocaleString('nb-NO')}</div>
                        <div className="text-muted small">{totalPoster} poster</div>
                    </div>
                </div>
            )}

            {error && <AlertBox classes="mt-3">{error}</AlertBox>}
        </div>
    );
};

export default ExpenseTable;
